import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '../../store';
import {
  Users, Plus, Search, Edit2, Trash2, X, Save, Loader2,
  Shield, Eye, EyeOff, UserPlus, ToggleLeft, ToggleRight,
  LayoutDashboard, Store, Package, ShoppingCart, Wallet,
  MessageCircle, Plug, Lock, Settings, Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_MODULES = [
  { key: 'dashboard', label: 'Genel Bakis', icon: LayoutDashboard, desc: 'Dashboard istatistiklerini gorebilir' },
  { key: 'dealers', label: 'Bayiler', icon: Store, desc: 'Bayi listesi ve yonetimi' },
  { key: 'products', label: 'Urunler', icon: Package, desc: 'Urun listesi ve yonetimi' },
  { key: 'orders', label: 'Siparisler', icon: ShoppingCart, desc: 'Siparis listesi ve durum yonetimi' },
  { key: 'payment_tracking', label: 'Odeme Takibi', icon: Wallet, desc: 'Odeme kayitlari ve takip' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, desc: 'WhatsApp mesajlasma yonetimi' },
  { key: 'integrations', label: 'Entegrasyonlar', icon: Plug, desc: 'API ayarlari ve entegrasyonlar' },
  { key: 'security', label: 'Guvenlik Loglari', icon: Lock, desc: 'Giris loglari ve guvenlik' },
  { key: 'settings', label: 'Sistem Ayarlari', icon: Settings, desc: 'Genel sistem ayarlari' },
];

export default function AdminPersonnelPage() {
  const { token } = useAuthStore();
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [updatingPerms, setUpdatingPerms] = useState(false);
  const [waStatus, setWaStatus] = useState(null);

  const [form, setForm] = useState({ name: '', email: '', password: '', permissions: [] });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchPersonnel(); }, []);

  // Fetch WhatsApp status
  useEffect(() => {
    const fetchWa = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/whatsapp/status`, { headers });
        setWaStatus(res.data);
      } catch {}
    };
    fetchWa();
  }, [token]);

  const fetchPersonnel = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/personnel`, { headers });
      setPersonnel(res.data || []);
    } catch { toast.error('Personel listesi yuklenemedi'); }
    finally { setLoading(false); }
  };

  const openAddModal = () => {
    setEditing(null);
    setForm({ name: '', email: '', password: '', permissions: [...PERMISSION_MODULES.map(m => m.key)] });
    setShowModal(true);
  };

  const openEditModal = (p) => {
    setEditing(p);
    setForm({ name: p.name, email: p.email, password: '', permissions: [...(p.permissions || [])] });
    setShowModal(true);
  };

  const togglePermission = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(k => k !== key)
        : [...prev.permissions, key]
    }));
  };

  const selectAllPerms = () => setForm(prev => ({ ...prev, permissions: PERMISSION_MODULES.map(m => m.key) }));
  const clearAllPerms = () => setForm(prev => ({ ...prev, permissions: [] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Isim ve email zorunludur'); return; }
    if (!editing && !form.password) { toast.error('Sifre zorunludur'); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await axios.put(`${API_URL}/api/admin/personnel/${editing.id}`, payload, { headers });
        toast.success('Personel guncellendi');
      } else {
        await axios.post(`${API_URL}/api/admin/personnel`, form, { headers });
        toast.success('Personel olusturuldu');
      }
      setShowModal(false);
      fetchPersonnel();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Islem basarisiz');
    } finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`"${p.name}" personelini silmek istediginize emin misiniz?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/personnel/${p.id}`, { headers });
      toast.success('Personel silindi');
      if (selectedPersonnel?.id === p.id) setSelectedPersonnel(null);
      fetchPersonnel();
    } catch { toast.error('Personel silinemedi'); }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API_URL}/api/admin/personnel/${p.id}`, { is_active: !p.is_active }, { headers });
      toast.success(p.is_active ? 'Personel deaktif edildi' : 'Personel aktif edildi');
      fetchPersonnel();
    } catch { toast.error('Islem basarisiz'); }
  };

  const updatePermissionsInline = async (personnelId, permissions) => {
    setUpdatingPerms(true);
    try {
      await axios.put(`${API_URL}/api/admin/personnel/${personnelId}/permissions`, { permissions }, { headers });
      toast.success('Izinler guncellendi');
      fetchPersonnel();
    } catch { toast.error('Izinler guncellenemedi'); }
    finally { setUpdatingPerms(false); }
  };

  const toggleInlinePermission = (p, key) => {
    const current = p.permissions || [];
    const updated = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    updatePermissionsInline(p.id, updated);
  };

  const filtered = personnel.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q);
  });

  // Detail view
  if (selectedPersonnel) {
    const p = personnel.find(x => x.id === selectedPersonnel.id) || selectedPersonnel;
    return (
      <div className="space-y-3 sm:space-y-6" data-testid="personnel-detail-view">
        <button onClick={() => setSelectedPersonnel(null)} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900" data-testid="back-to-personnel">
          <X className="w-4 h-4" />Personele Dön
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{p.name}</h2>
              <p className="text-sm text-gray-500">{p.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {p.is_active ? 'Aktif' : 'Pasif'}
              </span>
              <button onClick={() => openEditModal(p)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" data-testid="edit-personnel-btn">
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-500" />
            Modul Izinleri
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PERMISSION_MODULES.map(mod => {
              const hasPermission = (p.permissions || []).includes(mod.key);
              const Icon = mod.icon;
              return (
                <button
                  key={mod.key}
                  onClick={() => toggleInlinePermission(p, mod.key)}
                  disabled={updatingPerms}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                    hasPermission
                      ? 'border-green-300 bg-green-50 hover:bg-green-100'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100 opacity-60'
                  }`}
                  data-testid={`perm-toggle-${mod.key}`}
                >
                  <div className={`p-2 rounded-lg ${hasPermission ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-900">{mod.label}</p>
                    <p className="text-[11px] text-gray-500 truncate">{mod.desc}</p>
                  </div>
                  {hasPermission ? (
                    <Check className="w-5 h-5 text-green-600 shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-gray-300 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-personnel-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Personel Yönetimi</h1>
          <p className="text-sm text-gray-500 hidden sm:block">Personel ekleyin ve izinlerini yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          {/* WhatsApp Status Badge */}
          {waStatus && (
            <div className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium ${
              waStatus.status === 'connected' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
            }`} data-testid="wa-status-badge">
              <MessageCircle className="w-3.5 h-3.5" />
              {waStatus.status === 'connected' ? 'WhatsApp Bağlı' : 'WhatsApp Bağlı Değil'}
            </div>
          )}
          <button
            onClick={openAddModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1a224f] text-white rounded-xl text-sm font-medium hover:bg-[#12173a] transition-colors"
            data-testid="add-personnel-btn"
          >
            <UserPlus className="w-4 h-4" />Yeni Personel
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Personel ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#5b67a0]"
          data-testid="personnel-search"
        />
      </div>

      {/* Personnel List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <Users className="w-14 h-14 mx-auto text-gray-200 mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">Personel Bulunamadı</h3>
          <p className="text-sm text-gray-500">{search ? 'Arama kriterinize uygun personel yok' : 'Henuz personel eklenmemis'}</p>
        </div>
      ) : (
        <>
          {/* Desktop Table (lg+) */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-10 px-2 py-2.5 text-center text-xs font-semibold text-gray-500">#</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Personel</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Email</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">İzinler</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-24">Durum</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-20">Aktif</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, idx) => (
                  <tr key={p.id} className={`border-b border-gray-100 group transition-colors ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} data-testid={`personnel-row-${p.id}`}>
                    <td className="px-2 py-2 text-center text-gray-400 text-xs">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${p.is_active ? 'bg-red-100' : 'bg-gray-100'}`}>
                          <span className={`font-bold text-xs ${p.is_active ? 'text-[#1a224f]' : 'text-gray-400'}`}>{p.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{p.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setSelectedPersonnel(p)} className="text-[11px] text-gray-500 hover:text-gray-800 font-medium" data-testid={`manage-perms-${p.id}`}>Izinler</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={() => openEditModal(p)} className="text-[11px] text-gray-500 hover:text-gray-800 font-medium" data-testid={`edit-${p.id}`}>Duzenle</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={() => handleDelete(p)} className="text-[11px] text-red-400 hover:text-[#1a224f] font-medium" data-testid={`delete-${p.id}`}>Sil</button>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{p.email}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1 max-w-[400px]">
                        {(p.permissions || []).length > 0 ? (
                          (p.permissions || []).slice(0, 6).map(perm => {
                            const mod = PERMISSION_MODULES.find(m => m.key === perm);
                            if (!mod) return null;
                            const Icon = mod.icon;
                            return (
                              <span key={perm} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                <Icon className="w-2.5 h-2.5" />{mod.label}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-[11px] text-gray-400 italic">Izin yok</span>
                        )}
                        {(p.permissions || []).length > 6 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">+{(p.permissions || []).length - 6}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {p.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => toggleActive(p)} className="p-1 hover:bg-gray-100 rounded transition-colors" title={p.is_active ? 'Deaktif Et' : 'Aktif Et'} data-testid={`toggle-active-${p.id}`}>
                        {p.is_active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards (<lg) */}
          <div className="lg:hidden space-y-3">
            {filtered.map(p => (
              <div key={p.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4" data-testid={`personnel-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                      <span className="text-[#1a224f] font-bold text-sm">{p.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{p.name}</p>
                      <p className="text-xs text-gray-500 truncate">{p.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{p.is_active ? 'Aktif' : 'Pasif'}</span>
                    <button onClick={() => toggleActive(p)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg" title={p.is_active ? 'Deaktif Et' : 'Aktif Et'}>
                      {p.is_active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(p.permissions || []).length > 0 ? (
                    (p.permissions || []).map(perm => {
                      const mod = PERMISSION_MODULES.find(m => m.key === perm);
                      if (!mod) return null;
                      const Icon = mod.icon;
                      return (
                        <span key={perm} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                          <Icon className="w-2.5 h-2.5" />{mod.label}
                        </span>
                      );
                    })
                  ) : (
                    <span className="text-xs text-gray-400 italic">Hicbir izin atanmamis</span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2 pt-3 border-t border-gray-50">
                  <button onClick={() => setSelectedPersonnel(p)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#1a224f] bg-red-50 hover:bg-red-100 rounded-lg transition-colors">
                    <Shield className="w-3 h-3" />İzinleri Yönet
                  </button>
                  <button onClick={() => openEditModal(p)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleDelete(p)} className="p-1.5 text-red-400 hover:bg-[#eef0f7] rounded-lg">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-2 sm:p-4 pb-20 sm:pb-4" onClick={() => setShowModal(false)}>
          <div className="bg-white w-full sm:max-w-lg rounded-2xl shadow-xl max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
              <h2 className="text-lg font-bold text-gray-900">{editing ? 'Personel Duzenle' : 'Yeni Personel'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Ad Soyad</label>
                <input
                  type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#5b67a0]"
                  data-testid="modal-name-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                <input
                  type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#5b67a0]"
                  data-testid="modal-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{editing ? 'Yeni Sifre (bos birakirsaniz degismez)' : 'Sifre'}</label>
                <input
                  type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#5b67a0]"
                  placeholder={editing ? 'Degistirmek icin yazin...' : 'En az 8 karakter'}
                  data-testid="modal-password-input"
                />
              </div>

              {/* Permissions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-gray-500">Modul Izinleri</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={selectAllPerms} className="text-[10px] text-blue-600 hover:underline">Tumunu Sec</button>
                    <button type="button" onClick={clearAllPerms} className="text-[10px] text-[#1a224f] hover:underline">Tumunu Kaldir</button>
                  </div>
                </div>
                <div className="space-y-2">
                  {PERMISSION_MODULES.map(mod => {
                    const active = form.permissions.includes(mod.key);
                    const Icon = mod.icon;
                    return (
                      <button
                        key={mod.key}
                        type="button"
                        onClick={() => togglePermission(mod.key)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                          active ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50 opacity-60'
                        }`}
                        data-testid={`modal-perm-${mod.key}`}
                      >
                        <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-green-600' : 'text-gray-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{mod.label}</p>
                          <p className="text-[11px] text-gray-500">{mod.desc}</p>
                        </div>
                        {active ? <Check className="w-4 h-4 text-green-600 shrink-0" /> : <X className="w-4 h-4 text-gray-300 shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#1a224f] text-white rounded-xl text-sm font-medium hover:bg-[#12173a] disabled:opacity-50 transition-colors"
                  data-testid="modal-save-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editing ? 'Guncelle' : 'Olustur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
